import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Home, Loader2, Send, Trash2, UserPlus, Users } from "lucide-react";
import { DEFAULT_VIEWING_MESSAGE, DEFAULT_CALLBACK_MESSAGE } from "../../utils/propertyViewingEmailGenerator";

export const ViewingForm = ({ register, setValue, formValues, customerEmail, setCustomerEmail, sending, handleSendEmail, crewList, selectedCrewIds, setSelectedCrewIds, newCrewName, setNewCrewName, newCrewRole, setNewCrewRole, addCrewMember, removeCrewMember, saveViewingDraft, resetViewingForm }) => (
  <>
    {/* ADMIN CONSOLE Header */}
    <Card className="border-2 border-[#14B8A6] bg-black overflow-hidden" data-testid="viewing-admin-console">
      <div className="bg-gradient-to-r from-[#14B8A6] to-[#0D9488] px-3 py-2.5">
        <h3 className="font-bold text-black text-sm tracking-widest">ADMIN CONSOLE</h3>
        <p className="text-[10px] text-black/70 font-semibold">Schedule viewings. Verify your crew.</p>
      </div>
      <CardContent className="p-3 bg-zinc-950">
        <p className="text-[11px] text-zinc-400 leading-relaxed">
          Send a trust-building, <span className="text-[#14B8A6] font-bold">no-charge</span> property viewing email with the exact crew arriving at the customer's door — each with a <span className="text-[#D4AF37] font-bold">HomeGo Digital ID</span> and a public QR verification link.
        </p>
      </CardContent>
    </Card>

    {/* Viewing Schedule */}
    <Card className="border-2 border-zinc-800 bg-zinc-950">
      <div className="bg-zinc-900 px-3 py-2 flex items-center justify-between border-b border-zinc-800">
        <h3 className="font-semibold text-white text-sm flex items-center gap-2">
          <Home className="h-4 w-4 text-[#14B8A6]" /> {formValues.viewing_type === "callback" ? "Arrange a Callback" : "Arrange a Property Viewing"}
        </h3>
        <span className="text-[9px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full font-bold tracking-wider">{formValues.viewing_type === "callback" ? "FREE · NO OBLIGATION" : "COMPLIMENTARY · NO CHARGE"}</span>
      </div>
      <CardContent className="p-3 space-y-2.5">
        <div>
          <Label className="text-xs text-zinc-400">Appointment Type</Label>
          <Select value={formValues.viewing_type || "viewing"} onValueChange={(val) => {
            setValue("viewing_type", val);
            // Swap default trust message if user hasn't customised it
            const current = formValues.viewing_magic_words;
            if (val === "callback" && (current === DEFAULT_VIEWING_MESSAGE || !current)) {
              setValue("viewing_magic_words", DEFAULT_CALLBACK_MESSAGE);
            } else if (val === "viewing" && (current === DEFAULT_CALLBACK_MESSAGE || !current)) {
              setValue("viewing_magic_words", DEFAULT_VIEWING_MESSAGE);
            }
          }}>
            <SelectTrigger className="h-9 mt-1 bg-zinc-900 border-[#14B8A6]/60 text-white font-semibold" data-testid="viewing-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="viewing">🏠 Property Viewing</SelectItem>
              <SelectItem value="callback">📞 Callback</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-zinc-400">Customer Name</Label>
            <Input {...register("customer_name")} className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-customer-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Customer Email</Label>
            <Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-customer-email" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">{formValues.viewing_type === "callback" ? "Customer Mobile (we'll call this number)" : "Customer Mobile (for SMS reminder)"}</Label>
          <Input {...register("viewing_customer_mobile")} placeholder="07..." className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-customer-mobile" />
        </div>
        {formValues.viewing_type !== "callback" && (
          <div>
            <Label className="text-xs text-zinc-400">Property Address</Label>
            <Input {...register("viewing_property_address")} placeholder="12 High Street, Reading RG1 1AA" className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-property-address" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-zinc-400">Date</Label>
            <Input type="date" {...register("viewing_date")} className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-date" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Time</Label>
            <Input type="time" {...register("viewing_time")} className="h-8 mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="viewing-time" />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400">Magic Words (Trust Message)</Label>
            <button type="button" onClick={() => setValue("viewing_magic_words", formValues.viewing_type === "callback" ? DEFAULT_CALLBACK_MESSAGE : DEFAULT_VIEWING_MESSAGE)} className="text-[10px] text-[#14B8A6] hover:underline font-semibold">Reset to default</button>
          </div>
          <Textarea {...register("viewing_magic_words")} rows={5} className="mt-1 bg-zinc-900 border-zinc-700 text-white text-xs" data-testid="viewing-magic-words" />
        </div>
      </CardContent>
    </Card>

    {/* Crew Members */}
    {formValues.viewing_type !== "callback" && (
    <Card className="border-2 border-[#D4AF37]/50 bg-zinc-950">
      <div className="bg-zinc-900 px-3 py-2 border-b border-zinc-800">
        <h3 className="font-semibold text-white text-sm flex items-center gap-2">
          <Users className="h-4 w-4 text-[#D4AF37]" /> Assign Crew Members
        </h3>
      </div>
      <CardContent className="p-3 space-y-2">
        {crewList.map((m) => (
          <div key={m.id} className={`flex items-center gap-2 p-2 rounded-lg border ${selectedCrewIds.includes(m.id) ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-zinc-800 bg-zinc-900"}`}>
            <Checkbox
              checked={selectedCrewIds.includes(m.id)}
              onCheckedChange={(c) => setSelectedCrewIds((prev) => c ? [...prev, m.id] : prev.filter((id) => id !== m.id))}
              data-testid={`crew-checkbox-${m.id}`}
            />
            <div className="flex-1">
              <p className="text-sm text-white font-medium">{m.name}</p>
              <p className="text-[10px] text-zinc-500">{m.role}</p>
            </div>
            <span className="text-[9px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full font-bold">DBS</span>
            <button type="button" onClick={() => removeCrewMember(m.id)} className="text-zinc-600 hover:text-red-400" title="Remove crew member">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <Input value={newCrewName} onChange={(e) => setNewCrewName(e.target.value)} placeholder="New crew name..." className="h-8 flex-1 bg-zinc-900 border-zinc-700 text-white text-xs" data-testid="new-crew-name" />
          <Select value={newCrewRole} onValueChange={setNewCrewRole}>
            <SelectTrigger className="h-8 w-28 bg-zinc-900 border-zinc-700 text-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Mover">Mover</SelectItem>
              <SelectItem value="Supervisor">Supervisor</SelectItem>
              <SelectItem value="Driver">Driver</SelectItem>
              <SelectItem value="Team Leader">Team Leader</SelectItem>
            </SelectContent>
          </Select>
          <Button type="button" size="sm" onClick={addCrewMember} className="h-8 px-2 bg-[#D4AF37] text-black hover:bg-[#c9a532]" data-testid="add-crew-button">
            <UserPlus className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
    )}

    {/* Actions */}
    <div className="flex gap-2">
      <Button type="button" onClick={handleSendEmail} disabled={sending} className="flex-1 h-9 bg-[#14B8A6] hover:bg-[#0D9488] text-black font-bold text-xs" data-testid="send-viewing-email-button">
        {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
        {formValues.viewing_type === "callback" ? "Send Callback Email" : "Send Viewing Email"}
      </Button>
      <Button type="button" variant="outline" onClick={saveViewingDraft} className="h-9 text-xs border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800" data-testid="save-viewing-draft-button">
        Save Draft
      </Button>
      <Button type="button" variant="outline" onClick={resetViewingForm} className="h-9 text-xs border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800" data-testid="reset-viewing-button">
        Reset
      </Button>
    </div>
  </>
);
